import { useEffect, useState } from 'react';
import './App.css';
import digitalWerksLogo from './digitalwerks-logo.png';
import {
  trackCtaClick,
  trackFormStart,
  trackFormSubmit,
  trackServiceInterestSelected,
} from './analytics';
import { getRecaptchaToken } from './recaptcha';
import PersonalizedEmailImagesPage from './PersonalizedEmailImagesPage';

const navigationItems = [
  { label: 'How We Help', id: 'capabilities' },
  { label: 'Expertise', id: 'expertise' },
  { label: 'Process', id: 'approach' },
  { label: 'Contact', id: 'contact' },
  {
    label: 'Resources',
    href: 'https://blog.digitalwerks.com',
    external: true,
  },
];

const heroSignals = [
  'Strategy through implementation',
  'Measurement from the start',
  'Support beyond launch',
];

const foundationCards = [
  {
    title: 'The experience',
    description:
      'The website, application, donation form, or campaign your audience needs to use.',
  },
  {
    title: 'The platforms',
    description:
      'The content, fundraising, CRM, commerce, and marketing tools your team depends on.',
  },
  {
    title: 'The evidence',
    description:
      'The tracking, attribution, reporting, and testing that show what is actually working.',
  },
  {
    title: 'The operation',
    description:
      'The workflows, documentation, and ongoing decisions that keep the system useful after launch.',
  },
];

const serviceCards = [
  {
    label: '01',
    title: 'Digital Systems Diagnostic',
    analyticsCategory: 'systems_diagnostic',
    description:
      'Trace the website, integrations, analytics, fundraising platforms, workflows, risks, and ownership gaps—then turn the findings into a prioritized roadmap.',
    detail: 'Audit · architecture · roadmap',
  },
  {
    label: '02',
    title: 'Connected Experience Build',
    analyticsCategory: 'connected_experience',
    description:
      'Design and build websites, applications, donation experiences, commerce flows, and integrations with performance, accessibility, and measurement included.',
    detail: 'Web · software · integrations',
  },
  {
    label: '03',
    title: 'Measurement & Conversion Foundation',
    analyticsCategory: 'measurement_conversion',
    description:
      'Implement dependable analytics, campaign attribution, conversion tracking, reporting, and governance so teams can make decisions with evidence.',
    detail: 'GA4 · GTM · attribution',
  },
  {
    label: '04',
    title: 'Digital Operations Partnership',
    analyticsCategory: 'digital_operations',
    description:
      'Keep the system secure, supported, documented, measured, and improving with ongoing engineering and senior technical guidance.',
    detail: 'Support · optimization · stewardship',
  },
];

const expertiseGroups = [
  {
    title: 'Fundraising Platforms',
    description:
      'Operational experience across donation and engagement systems where tracking quality directly affects fundraising outcomes.',
    items: [
      'Blackbaud Luminate forms and Online Express',
      'Classy and qGiv conversion tracking',
      'RaiseDonors, Charity Engine, and SimpleGive workflows',
      'DonorPerfect donation instrumentation',
    ],
  },
  {
    title: 'Measurement Stack',
    description:
      'Implementation experience grounded in event design, validation, attribution, and reporting—not abstract dashboards.',
    items: [
      'Google Tag Manager requirements and setup',
      'GA4 transition planning and reporting structure',
      'UTM campaign governance and Search Console setup',
      'Meta Conversion API support',
    ],
  },
  {
    title: 'Operational Enablement',
    description:
      'Delivery extends beyond launch to the systems, testing, and documentation teams need to keep moving.',
    items: [
      'Salesforce Marketing Cloud split testing',
      'Iframe and custom-domain tracking support',
      'Browser cache and history troubleshooting',
      'Training and ongoing support workflows',
    ],
  },
];

const audienceCards = [
  {
    label: 'Primary',
    title: 'Nonprofit teams',
    description:
      'For fundraising, marketing, communications, and digital leaders whose supporter experience depends on connected platforms and dependable measurement.',
  },
  {
    label: 'Primary',
    title: 'Agency partners',
    description:
      'For agencies that need senior engineering, WordPress, analytics, integration, or nonprofit-platform depth without adding another handoff problem.',
  },
  {
    label: 'Also a fit',
    title: 'Growth-stage businesses',
    description:
      'For teams whose website, CRM, reporting, commerce, or internal workflows have outgrown disconnected vendors and improvised fixes.',
  },
];

const experienceCards = [
  {
    label: 'Fundraising journeys',
    title: 'Connect the conversion path',
    description:
      'Work across donation forms, campaign pages, embedded experiences, custom domains, and the tracking required to understand supporter action.',
  },
  {
    label: 'Measurement foundations',
    title: 'Make the signal dependable',
    description:
      'Plan events, configure tags, validate attribution, and establish reporting structures that teams can use after implementation.',
  },
  {
    label: 'Operational enablement',
    title: 'Leave the team stronger',
    description:
      'Support testing, documentation, training, troubleshooting, and repeatable workflows so progress does not depend on one launch.',
  },
];

const partnershipItems = [
  'Senior technical guidance close to the work',
  'Engineering, analytics, and support in one operating rhythm',
  'Documentation that reduces dependency and preserves decisions',
  'Ongoing optimization based on evidence—not a rebuild cycle',
];

const processSteps = [
  {
    step: 'Discover',
    title: 'Clarify the goal',
    description:
      'Identify business objectives, user friction, technical constraints, and the signal that matters after launch.',
  },
  {
    step: 'Design',
    title: 'Shape the experience',
    description:
      'Translate strategy into an interface that feels modern, intuitive, and aligned with the audience you need to move.',
  },
  {
    step: 'Build',
    title: 'Connect the stack',
    description:
      'Ship the front end, integrations, analytics, automation, and content systems as one coordinated delivery.',
  },
  {
    step: 'Optimize',
    title: 'Keep improving',
    description:
      'Use support, measurement, and iteration to refine performance instead of treating launch as the finish line.',
  },
];

const leadEndpoint =
  process.env.REACT_APP_APPOINTMENT_ENDPOINT ||
  'https://api.digitalwerks.com/mautic/lead';
const contactEmail = 'donnie@digitalwerks.com';
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const successMessage = 'Message received. DigitalWerks will follow up shortly.';
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const leadDefaults = {
  emailid: 32,
  responder: 33,
  segment: 27,
  emailContactId: 190789,
};
const contactFormId = 'digitalwerks-contact-form';
const contactFormName = 'DigitalWerks Website Contact Form';
const contactFormLocation = 'contact_section';
const contactLeadType = 'contact_inquiry';
const recaptchaAction = 'contact_form_submit';

const routes = {
  home: '/',
  personalizedEmailImages: '/personalized-email-images',
  privacy: '/privacy-policy',
  terms: '/terms-of-service',
};

const getPageFromPath = (pathname) => {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';

  if (normalizedPath === routes.privacy) {
    return 'privacy';
  }

  if (normalizedPath === routes.terms) {
    return 'terms';
  }

  if (normalizedPath === routes.personalizedEmailImages) {
    return 'personalizedEmailImages';
  }

  return 'home';
};

const getResponseMessage = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const payload = await response.json().catch(() => null);
    return payload?.message || '';
  }

  const text = await response.text().catch(() => '');
  return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const getCurrentPageLocation = () =>
  typeof window !== 'undefined' ? window.location.href : 'https://digitalwerks.com/';

const buildAbsoluteUrl = (destination) => {
  if (!destination || typeof window === 'undefined') {
    return undefined;
  }

  try {
    return new URL(destination, window.location.href).toString();
  } catch {
    return destination;
  }
};

const getLinkContext = (destination) => {
  const absoluteUrl = buildAbsoluteUrl(destination);

  if (!absoluteUrl) {
    return {};
  }

  try {
    const parsedUrl = new URL(absoluteUrl);
    return {
      link_url: absoluteUrl,
      link_domain: parsedUrl.hostname || undefined,
    };
  } catch {
    return {
      link_url: absoluteUrl,
    };
  }
};

const buildContactFormTrackingParams = () => ({
  form_id: contactFormId,
  form_name: contactFormName,
  form_destination: leadEndpoint,
  form_location: contactFormLocation,
  lead_type: contactLeadType,
  page_location: getCurrentPageLocation(),
});

const buildCtaTrackingParams = ({
  ctaText,
  ctaLocation,
  ctaDestination,
}) => ({
  cta_text: ctaText,
  cta_location: ctaLocation,
  cta_destination: ctaDestination,
  page_location: getCurrentPageLocation(),
  ...getLinkContext(ctaDestination),
});

const buildServiceInterestTrackingParams = ({
  serviceName,
  serviceCategory,
}) => ({
  service_name: serviceName,
  service_category: serviceCategory,
  page_location: getCurrentPageLocation(),
});

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(() =>
    typeof window === 'undefined' ? 'home' : getPageFromPath(window.location.pathname)
  );
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    message: '',
    error: false,
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [hasTrackedFormStart, setHasTrackedFormStart] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getPageFromPath(window.location.pathname));
      setMobileMenuOpen(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const pageMetadata = {
      home: {
        title: 'Digital Systems, Web Development & Analytics | DigitalWerks',
        description: 'DigitalWerks connects websites, software, analytics, fundraising platforms, and operational workflows for nonprofits, agencies, and growth-minded teams.',
        path: '/',
      },
      privacy: {
        title: 'Privacy Policy | DigitalWerks',
        description: 'Learn how DigitalWerks handles information submitted through digitalwerks.com and related service inquiries.',
        path: '/privacy-policy',
      },
      terms: {
        title: 'Terms of Service | DigitalWerks',
        description: 'Review the terms that govern digitalwerks.com and DigitalWerks professional services.',
        path: '/terms-of-service',
      },
      personalizedEmailImages: {
        title: 'Personalized Email Images — $5 per 1,000 Recipients | DigitalWerks',
        description: 'Put names, offers, promo codes, dates, amounts, and messages inside personalized email creative using the ESP you already have. $5 per 1,000 recipients.',
        path: '/personalized-email-images',
      },
    };
    const metadata = pageMetadata[currentPage];
    const canonicalUrl = `https://digitalwerks.com${metadata.path}`;
    const setMeta = (selector, attribute, value) => {
      const element = document.querySelector(selector);
      if (element) element.setAttribute(attribute, value);
    };

    document.title = metadata.title;
    setMeta('meta[name="description"]', 'content', metadata.description);
    setMeta('meta[property="og:title"]', 'content', metadata.title);
    setMeta('meta[property="og:description"]', 'content', metadata.description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[name="twitter:title"]', 'content', metadata.title);
    setMeta('meta[name="twitter:description"]', 'content', metadata.description);
    setMeta('link[rel="canonical"]', 'href', canonicalUrl);
  }, [currentPage]);

  const navigateToPath = (path) => {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }

    setCurrentPage(getPageFromPath(path));
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    if (currentPage !== 'home') {
      window.history.pushState({}, '', id === 'top' ? routes.home : `${routes.home}#${id}`);
      setCurrentPage('home');
      window.requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      });
    } else {
      if (id === 'top') {
        window.history.pushState({}, '', routes.home);
      } else {
        window.history.pushState({}, '', `${routes.home}#${id}`);
      }

      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }

    setMobileMenuOpen(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const trackPrimaryCta = ({ ctaText, ctaLocation, ctaDestination }) => {
    trackCtaClick(
      buildCtaTrackingParams({
        ctaText,
        ctaLocation,
        ctaDestination,
      }),
    );
  };

  const handleContactFormStart = () => {
    if (hasTrackedFormStart) {
      return;
    }

    trackFormStart(buildContactFormTrackingParams());
    setHasTrackedFormStart(true);
  };

  const handleServiceInterestClick = (card) => {
    trackServiceInterestSelected(
      buildServiceInterestTrackingParams({
        serviceName: card.title,
        serviceCategory: card.analyticsCategory,
      }),
    );
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    const submittedForm = new FormData(event.currentTarget);
    const submittedValues = {
      name: String(submittedForm.get('name') || '').trim(),
      email: String(submittedForm.get('email') || '').trim(),
      company: String(submittedForm.get('company') || '').trim(),
      phone: String(submittedForm.get('phone') || '').trim(),
      message: String(submittedForm.get('message') || '').trim(),
    };

    if (!submittedValues.name || !submittedValues.email || !submittedValues.message) {
      setFormStatus({
        submitted: true,
        message: 'Please complete the required fields and try again.',
        error: true,
      });
      return;
    }

    if (!emailPattern.test(submittedValues.email)) {
      setFormStatus({
        submitted: true,
        message: 'Enter a valid email address and try again.',
        error: true,
      });
      return;
    }

    setFormSubmitting(true);
    setFormStatus({ submitted: false, message: '', error: false });

    const messageLines = [
      `Name: ${submittedValues.name}`,
      `Email: ${submittedValues.email}`,
      submittedValues.company ? `Company: ${submittedValues.company}` : '',
      submittedValues.phone ? `Phone: ${submittedValues.phone}` : '',
      `Project details: ${submittedValues.message}`,
    ].filter(Boolean);

    try {
      const recaptchaToken = await getRecaptchaToken(recaptchaAction);
      const payload = {
        ...submittedValues,
        ...leadDefaults,
        domain: hostname,
        goal: 'DigitalWerks website inquiry',
        project_details: submittedValues.message,
        message: messageLines.join('\n'),
        source: 'DigitalWerks Website Contact Form',
        submitted_at: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        recaptcha_token: recaptchaToken,
        recaptcha_action: recaptchaAction,
      };

      const response = await fetch(leadEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseMessage = await getResponseMessage(response);

      if (!response.ok) {
        throw new Error(responseMessage || 'Unable to submit your request right now.');
      }

      trackFormSubmit(buildContactFormTrackingParams());
      setFormStatus({
        submitted: true,
        message: successMessage,
        error: false,
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
      });
      setHasTrackedFormStart(false);
    } catch (error) {
      setFormStatus({
        submitted: true,
        message: error.message || 'Something went wrong. Please try again in a moment.',
        error: true,
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="App">
      <div className="page-aura page-aura-left" aria-hidden="true" />
      <div className="page-aura page-aura-right" aria-hidden="true" />

      <header className="site-header">
        <nav className="navbar" aria-label="Primary navigation">
          <button
            type="button"
            className="brandmark"
            onClick={() => scrollToSection('top')}
            aria-label="Go to top of page"
          >
            <img
              className="brand-logo"
              src={digitalWerksLogo}
              alt="DigitalWerks"
              width="1235"
              height="470"
            />
          </button>

          <button
            type="button"
            className={`mobile-menu-btn ${mobileMenuOpen ? 'is-open' : ''}`}
            aria-label="Toggle navigation"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((previous) => !previous)}
          >
            <span />
            <span />
            <span />
          </button>

          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            {navigationItems.map((item) => (
              <li key={item.id || item.href}>
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <button type="button" onClick={() => scrollToSection(item.id)}>
                    {item.label}
                  </button>
                )}
              </li>
            ))}
            <li className="nav-cta-item">
              <button
                type="button"
                className="nav-cta"
                onClick={() => {
                  trackPrimaryCta({
                    ctaText: 'Start a Project',
                    ctaLocation: 'header_navigation',
                    ctaDestination: '#contact',
                  });
                  scrollToSection('contact');
                }}
              >
                Start a Project
              </button>
            </li>
          </ul>
        </nav>
      </header>

      <main>
        {currentPage === 'privacy' && <PrivacyPolicyPage />}
        {currentPage === 'terms' && <TermsOfServicePage />}
        {currentPage === 'personalizedEmailImages' && <PersonalizedEmailImagesPage />}
        {currentPage === 'home' && (
          <>
            <section id="top" className="hero-section">
              <div className="hero-media" aria-hidden="true">
                <img
                  src="/images/digitalwerks-connected-system-hero.jpg"
                  alt=""
                  width="1774"
                  height="887"
                  fetchPriority="high"
                />
              </div>
              <div className="hero-inner">
                <div className="hero-copy">
                  <p className="section-kicker">Websites, data + digital operations</p>
                  <h1>Make the whole digital system work.</h1>
                  <p className="hero-lead">
                    DigitalWerks connects the experiences your audience sees with the
                    platforms, data, fundraising tools, and workflows your team depends
                    on—so you can launch confidently, measure what matters, and keep
                    improving.
                  </p>

                  <div className="hero-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        trackPrimaryCta({
                          ctaText: 'Plan Your Project',
                          ctaLocation: 'hero_section',
                          ctaDestination: '#contact',
                        });
                        scrollToSection('contact');
                      }}
                    >
                      Plan Your Project
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => scrollToSection('capabilities')}
                    >
                      See How We Help
                    </button>
                  </div>

                  <div className="hero-signals">
                    {heroSignals.map((signal) => (
                      <span key={signal} className="signal-pill">
                        {signal}
                      </span>
                    ))}
                  </div>
                </div>

                <aside className="hero-proof">
                  <span className="hero-proof-dot" aria-hidden="true" />
                  <div>
                    <strong>Turn complexity into forward motion</strong>
                    <p>Strategy, implementation, measurement, and support in one operating rhythm.</p>
                  </div>
                </aside>
              </div>
            </section>

            <section className="section section-foundations">
              <div className="section-heading">
                <p className="section-kicker">Why DigitalWerks exists</p>
                <h2>Digital work rarely breaks in only one place.</h2>
                <p className="section-lead">
                  A slow page may be a content problem. A missing conversion may begin
                  inside an embedded form. A dashboard can be accurate and still fail
                  to answer the decision in front of the team. DigitalWerks was built
                  around a practical conviction: treat the experience, platforms,
                  evidence, and operation as one connected system.
                </p>
              </div>

              <div className="foundation-grid">
                {foundationCards.map((card) => (
                  <article key={card.title} className="foundation-card">
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                  </article>
                ))}
              </div>
            </section>

            <section id="capabilities" className="section section-cream">
              <div className="section-heading">
                <p className="section-kicker">How we help</p>
                <h2>Start with the problem—not a predetermined deliverable.</h2>
                <p className="section-lead">
                  Four engagement paths bring strategy, implementation, measurement,
                  and support together while keeping the work anchored to the outcome
                  your team needs.
                </p>
              </div>

              <div className="service-grid">
                {serviceCards.map((card) => (
                  <button
                    key={card.title}
                    type="button"
                    className="service-card service-card-button"
                    onClick={() => handleServiceInterestClick(card)}
                  >
                    <div className="service-card-header">
                      <span className="service-card-label">{card.label}</span>
                      <h3>{card.title}</h3>
                    </div>
                    <p>{card.description}</p>
                    <span className="service-card-detail">{card.detail}</span>
                  </button>
                ))}
              </div>
            </section>

            <section id="expertise" className="section section-dark">
              <div className="section-heading section-heading-dark">
                <p className="section-kicker">Execution Depth</p>
                <h2>Platform knowledge that shortens the path to done.</h2>
                <p className="section-lead">
                  Bring in a partner who can work inside the analytics, fundraising,
                  marketing, content, and integration constraints your team already
                  has—without turning platform names into empty credentials.
                </p>
              </div>

              <div className="expertise-grid">
                {expertiseGroups.map((group) => (
                  <article key={group.title} className="expertise-card">
                    <h3>{group.title}</h3>
                    <p>{group.description}</p>
                    <ul>
                      {group.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>

              <aside className="ai-callout">
                <p className="section-kicker">AI Outlook</p>
                <h3>Put AI where it creates real leverage.</h3>
                <p>
                  Clean data and connected workflows make useful AI possible. We
                  apply it where it can improve research, reporting, documentation,
                  and support—always with people in control.
                </p>
              </aside>
            </section>

            <section id="approach" className="section section-approach">
              <div className="approach-layout">
                <div className="approach-intro">
                  <p className="section-kicker">Who It Serves Best</p>
                  <h2>Built for teams whose website is only part of the system.</h2>
                  <p className="section-lead">
                    The strongest engagements connect what your audience sees with
                    the fundraising, marketing, content, and reporting workflows your
                    team uses every day.
                  </p>

                  <div className="audience-grid">
                    {audienceCards.map((card) => (
                      <article key={card.title} className="audience-card">
                        <span>{card.label}</span>
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                      </article>
                    ))}
                  </div>
                </div>

                <div className="process-panel">
                  <p className="section-kicker">How DigitalWerks Works</p>
                  <div className="process-list">
                    {processSteps.map((step) => (
                      <article key={step.step} className="process-card">
                        <span className="process-step">{step.step}</span>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="section section-experience" aria-labelledby="experience-heading">
              <div className="section-heading">
                <p className="section-kicker">Implementation experience</p>
                <h2 id="experience-heading">Evidence from the work—not borrowed authority.</h2>
                <p className="section-lead">
                  DigitalWerks brings verified implementation experience across
                  fundraising journeys, measurement foundations, and the operational
                  work that makes both dependable. No partner badges or inflated
                  metrics required.
                </p>
              </div>
              <div className="experience-grid">
                {experienceCards.map((card) => (
                  <article key={card.title} className="experience-card">
                    <span>{card.label}</span>
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                  </article>
                ))}
              </div>
            </section>

            <section className="section section-partnership">
              <div className="partnership-copy">
                <p className="section-kicker">Beyond launch</p>
                <h2>A digital operation should get stronger over time.</h2>
                <p>
                  DigitalWerks stays close enough to understand why decisions were
                  made, how the system behaves, and what the evidence says next.
                  That continuity turns one project into an operating advantage.
                </p>
              </div>
              <ul className="partnership-list">
                {partnershipItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section id="contact" className="section section-contact">
              <div className="contact-layout">
                <div className="contact-copy">
                  <p className="section-kicker">Contact</p>
                  <h2>Tell us what needs to work better.</h2>
                  <p className="section-lead">
                    Whether you need a new website, cleaner analytics, a stronger
                    fundraising stack, or a better connected workflow, we’ll help
                    you define the right next step.
                  </p>

                  <div className="contact-details">
                    <a
                      href={`mailto:${contactEmail}`}
                      onClick={() =>
                        trackPrimaryCta({
                          ctaText: contactEmail,
                          ctaLocation: 'contact_details',
                          ctaDestination: `mailto:${contactEmail}`,
                        })
                      }
                    >
                      {contactEmail}
                    </a>
                    <a
                      href="tel:+17707891430"
                      onClick={() =>
                        trackPrimaryCta({
                          ctaText: '+1 770 789 1430',
                          ctaLocation: 'contact_details',
                          ctaDestination: 'tel:+17707891430',
                        })
                      }
                    >
                      +1 770 789 1430
                    </a>
                    <p>3760 Sixes Rd Suite 126, Canton, GA 30114</p>
                    <p>NDA-friendly engagements are available.</p>
                  </div>
                </div>

                <form
                  className="contact-form"
                  onSubmit={handleFormSubmit}
                  onFocusCapture={handleContactFormStart}
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Your name"
                        autoComplete="name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        placeholder="you@company.com"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="company">Company</label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleFormChange}
                        placeholder="Organization or team"
                        autoComplete="organization"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleFormChange}
                        placeholder="Best number"
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Project details</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      placeholder="What are you building, improving, or trying to untangle?"
                      rows="6"
                      required
                    />
                  </div>

                  {formStatus.submitted && (
                    <div
                      className={`form-message ${
                        formStatus.error ? 'error' : 'success'
                      }`}
                      role="status"
                    >
                      {formStatus.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={formSubmitting}
                    onClick={() =>
                      trackPrimaryCta({
                        ctaText: 'Request a Project Conversation',
                        ctaLocation: 'contact_form',
                        ctaDestination: leadEndpoint,
                      })
                    }
                  >
                    {formSubmitting ? 'Sending...' : 'Request a Project Conversation'}
                  </button>
                </form>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="site-footer">
        <p>© {currentYear} DigitalWerks Innovations. Make the whole system work.</p>
        <nav className="footer-nav" aria-label="Legal navigation">
          <a
            href={routes.privacy}
            onClick={(event) => {
              event.preventDefault();
              navigateToPath(routes.privacy);
            }}
          >
            Privacy Policy
          </a>
          <a
            href={routes.terms}
            onClick={(event) => {
              event.preventDefault();
              navigateToPath(routes.terms);
            }}
          >
            Terms of Service
          </a>
        </nav>
      </footer>
    </div>
  );
}

function PrivacyPolicyPage() {
  return (
    <section className="section legal-page">
      <div className="section-container">
        <p className="section-kicker">Legal</p>
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: July 31, 2026</p>
        <p>This Privacy Policy explains how DigitalWerks collects, uses, and safeguards information submitted through digitalwerks.com and in connection with inquiries about our professional services.</p>
        <h2>Information We Collect</h2>
        <p>1. Information you provide</p>
        <ul>
          <li>Contact details such as your name, email address, organization, and phone number.</li>
          <li>Project details and other information you choose to include in a form, email, or service inquiry.</li>
          <li>Communications and materials you provide while discussing or receiving professional services.</li>
        </ul>
        <p>2. Information collected automatically</p>
        <p>When you visit digitalwerks.com, we may automatically collect:</p>
        <ul>
          <li>Device and browser type</li>
          <li>IP address</li>
          <li>Referring/exit pages</li>
          <li>Date and time of visits</li>
          <li>Standard analytics information (via tools such as Google Analytics or similar services)</li>
        </ul>
        <p>This information helps us understand website usage, improve functionality, measure performance, and protect the site from abuse.</p>
        <h2>How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Respond to inquiries and discuss potential projects</li>
          <li>Provide and support agreed professional services</li>
          <li>Improve our website, communications, and service experience</li>
          <li>Maintain security, prevent spam and abuse, and troubleshoot technical issues</li>
          <li>Comply with legal obligations and enforce applicable agreements</li>
        </ul>
        <h2>Data Sharing and Disclosure</h2>
        <ul>
          <li>Service providers: We may share limited information with providers that support website hosting, analytics, form processing, communications, or service delivery.</li>
          <li>Legal Compliance: We may disclose data if required by law, subpoena, or legal process.</li>
          <li>No sale of data: DigitalWerks does not sell, rent, or trade your personal information.</li>
        </ul>
        <h2>Data Security</h2>
        <p>We use reasonable administrative and technical safeguards, including encryption in transit, to protect information handled through our website and services. No method of transmission or storage is completely secure.</p>
        <h2>Data Retention</h2>
        <ul>
          <li>Website analytics data is retained in aggregate form for trend analysis.</li>
          <li>Inquiry and project information is retained only as long as reasonably necessary for communication, service delivery, recordkeeping, security, and legal obligations.</li>
          <li>You may contact us to request access to, correction of, or deletion of personal information, subject to applicable legal requirements.</li>
        </ul>
        <h2>Your Choices</h2>
        <ul>
          <li>Cookies & Analytics: You may opt out of non-essential cookies through your browser settings.</li>
          <li>Communication Preferences: You may unsubscribe from marketing emails using the link in the message.</li>
          <li>Privacy Requests: You may contact us regarding the personal information we maintain about you.</li>
        </ul>
        <h2>Children's Privacy</h2>
        <p>DigitalWerks is not directed toward individuals under the age of 16. We do not knowingly collect data from children.</p>
        <h2>Changes to This Policy</h2>
        <p>We may update this Privacy Policy periodically. Significant changes will be communicated via email or a prominent notice on our website.</p>
        <h2>Contact Us</h2>
        <p>If you have questions about this Privacy Policy or how your information is handled, please contact us:</p>
        <p>DigitalWerks<br />donnie@digitalwerks.com<br />digitalwerks.com</p>
      </div>
    </section>
  );
}

function TermsOfServicePage() {
  return (
    <section className="section legal-page">
      <div className="section-container">
        <p className="section-kicker">Legal</p>
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: July 31, 2026</p>
        <p>These Terms govern your use of digitalwerks.com and apply generally to DigitalWerks professional services unless a separate written agreement states otherwise. By using this website, you agree to these Terms.</p>
        <h2>1. Eligibility</h2>
        <p>You must be at least 18 years old and legally capable of entering into contracts to engage DigitalWerks for professional services.</p>
        <h2>2. Website Use</h2>
        <ul>
          <li>You may use this website only for lawful purposes.</li>
          <li>You may not attempt to disrupt, overload, probe, or gain unauthorized access to the website or its supporting systems.</li>
          <li>You may not use automated systems to submit spam, scrape protected content, or interfere with other visitors.</li>
        </ul>
        <h2>3. Professional Services</h2>
        <ul>
          <li>DigitalWerks provides digital strategy, design, development, analytics, integration, and operational support services.</li>
          <li>Specific scope, deliverables, schedules, responsibilities, fees, and payment terms may be governed by a proposal, statement of work, order form, or other written agreement.</li>
          <li>If a separate written agreement conflicts with these Terms regarding professional services, the separate agreement controls.</li>
        </ul>
        <h2>4. Client Responsibilities</h2>
        <ul>
          <li>You are responsible for providing accurate information, timely feedback, required approvals, and authorized access needed to perform agreed services.</li>
          <li>You represent that you have the right to provide any content, data, credentials, or materials supplied to DigitalWerks.</li>
          <li>You remain responsible for your organization's legal, privacy, accessibility, and regulatory obligations unless a written agreement expressly states otherwise.</li>
        </ul>
        <h2>5. Intellectual Property</h2>
        <ul>
          <li>DigitalWerks retains ownership of pre-existing tools, methods, code, templates, documentation, and know-how unless a written agreement states otherwise.</li>
          <li>Ownership and licensing of project deliverables are governed by the applicable written service agreement.</li>
          <li>Website content, branding, and materials may not be copied or redistributed without permission except as allowed by law.</li>
        </ul>
        <h2>6. Third-Party Services and Links</h2>
        <ul>
          <li>This website and DigitalWerks projects may reference or depend on third-party platforms, software, hosting, or services.</li>
          <li>DigitalWerks is not responsible for third-party availability, policies, content, pricing, or changes outside our control.</li>
        </ul>
        <h2>7. Fees and Payment</h2>
        <p>Professional-service fees, deposits, expenses, invoicing, payment schedules, and cancellation terms are established in the applicable proposal or written agreement.</p>
        <h2>8. Disclaimer of Warranties</h2>
        <p>The website is provided "as is" and "as available" without warranties of any kind. Warranties, if any, for professional services are limited to those expressly stated in the applicable written agreement.</p>
        <h2>9. Limitation of Liability</h2>
        <p>To the maximum extent permitted by law:</p>
        <ul>
          <li>DigitalWerks and its affiliates will not be liable for indirect, incidental, special, consequential, or punitive damages.</li>
          <li>Liability related to professional services is governed by the applicable written agreement.</li>
        </ul>
        <h2>10. Governing Law</h2>
        <p>These Terms are governed by the laws of the State of Georgia, USA, without regard to conflict of law principles. Disputes will be resolved in the courts located in Cherokee County, Georgia.</p>
        <h2>11. Changes to Terms</h2>
        <p>We may update these Terms periodically. Continued use of digitalwerks.com after an update constitutes acceptance of the revised Terms.</p>
        <h2>12. Contact</h2>
        <p>If you have questions about these Terms, please contact us:</p>
        <p>DigitalWerks<br />donnie@digitalwerks.com<br />digitalwerks.com</p>
      </div>
    </section>
  );
}

export default App;
